/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useCallback, useContext, useMemo } from "react";

// components
import CartUIWrapper from "./cart-ui-wrapper";
import CartError from "@cart-client/components/cart-error";
import { Shimmer } from "@cart-client/components/shimmer";

// core-ui
import { GenericError, isNetworkError } from "@swiggy-private/core-ui";

// cart-ui
import { CART_GENERIC_EVENT_CASES, CART_STATUS_CODES } from "@swiggy-private/cart-ui";

// constants
import { LOGIN, RETRY } from "@cart-client/constants/TextDict";
import { ERROR_SCREEN_RETRY_WAIT_TIME_SECS, HTTP_STATUS_CODES } from "@cart-client/constants/index";

// types
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartError as CartApiError } from "@cart-client/hooks/useFetchCart";
import { isEmpty } from "@swiggy-private/js-utils";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import {
    goToHome,
    closeWebview,
    recordGenericErrorNREvent,
} from "@swiggy-private/checkout-core";

interface CartLoaderAndErrorRendererProps {
    disableRedirectionToMenu: boolean | undefined;
    loading: boolean;
    cartApiError?: CartApiError;
}

export function CartLoaderAndErrorRenderer(props: CartLoaderAndErrorRendererProps): React.ReactElement | null {
    const { cartState } = useContext(CartContext);
    const { updateCartInProgress, updateCartError, reloadCart } = useContext(UpdateCartContext);

    const { reviewedCart: cartResponse } = cartState;

    const { disableRedirectionToMenu, loading, cartApiError } = props;

    const showNetworkConnectivityScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            !loading && cartApiError?.errorPayload && isNetworkError(cartApiError?.errorPayload)
        );
    }, [cartApiError, loading]);

    const show429ErrorScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            // this is for when we update cart after load and it fails
            (!loading &&
                !isEmpty(cartApiError) &&
                cartApiError.httpStatusCode == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) ||
            (!updateCartInProgress && updateCartError?.httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS)
        );
    }, [cartApiError, loading, updateCartError?.httpStatusCode, updateCartInProgress]);

    const onImpressionTooManyRequestsErrorPage = useCallback(
        () => recordGenericErrorNREvent({ errorContext: CART_GENERIC_EVENT_CASES.TOO_MANY_REQUESTS }),
        [],
    );
    const onImpressionNullCartResponseErrorPage = useCallback(
        () => recordGenericErrorNREvent({ errorContext: CART_GENERIC_EVENT_CASES.NULL_CART_RESPONSE }),
        [],
    );

    if (showNetworkConnectivityScreen) {
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} isCartError>
                <GenericError
                    action={{
                        text: RETRY,
                        onClick: () => {
                            window.location.reload();
                        },
                    }}
                />
            </CartUIWrapper>
        );
    }

    if (show429ErrorScreen) {
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} isCartError>
                <GenericError
                    pageImpressionEvent={onImpressionTooManyRequestsErrorPage}
                    action={null}
                    primaryAction={{
                        timeDisabledFor: ERROR_SCREEN_RETRY_WAIT_TIME_SECS,
                        text: RETRY,
                        onClick: () => {
                            window.location.reload();
                        },
                    }}
                />
            </CartUIWrapper>
        );
    }

    if (!cartResponse || loading) {
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} isCartLoading>
                <Shimmer />
            </CartUIWrapper>
        );
    }

    const isSessionExpiredError = cartResponse.statusCode === CART_STATUS_CODES.SESSION_EXPIRED;
    if (!cartResponse.data || cartResponse.statusCode === CART_STATUS_CODES.FAILURE || isSessionExpiredError) {
        const ctaAction = isSessionExpiredError ? "goToLogin" : cartResponse.ctaAction;
        const ctaText = isSessionExpiredError ? LOGIN : cartResponse.ctaText;
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} isCartError>
                {cartResponse !== null ? (
                    <CartError
                        titleMessage={cartResponse.titleMessage}
                        statusMessage={cartResponse.statusMessage}
                        stayOnCartScreen={cartResponse.stayOnCartScreen}
                        goToHome={goToHome}
                        goBack={closeWebview}
                        ctaAction={ctaAction as any}
                        ctaText={ctaText}
                        isPrimaryAction
                    />
                ) : (
                    <GenericError
                        pageImpressionEvent={onImpressionNullCartResponseErrorPage}
                        action={{ onClick: reloadCart }}
                    />
                )}
            </CartUIWrapper>
        );
    }

    return null;
}

export default memo(CartLoaderAndErrorRenderer);
